const cache = new Map<string, RegExp>();

/**
 * Minimal glob: `**` matches across `/`, `*` matches within a segment, `?` one char.
 * A trailing `/**` also matches the directory itself.
 */
export function globToRegExp(glob: string): RegExp {
  const cached = cache.get(glob);
  if (cached) return cached;
  let source = "";
  for (let i = 0; i < glob.length; i++) {
    const ch = glob[i]!;
    if (ch === "*") {
      if (glob[i + 1] === "*") {
        const slashAfter = glob[i + 2] === "/";
        source += slashAfter ? "(?:.*/)?" : ".*";
        i += slashAfter ? 2 : 1;
      } else {
        source += "[^/]*";
      }
    } else if (ch === "?") {
      source += "[^/]";
    } else {
      source += ch.replace(/[.+^${}()|[\]\\]/g, "\\$&");
    }
  }
  if (source.endsWith("/.*")) source = `${source.slice(0, -3)}(?:/.*)?`;
  const re = new RegExp(`^${source}$`);
  cache.set(glob, re);
  return re;
}

export function matchGlob(glob: string, path: string): boolean {
  return globToRegExp(normalize(glob)).test(normalize(path));
}

function normalize(value: string): string {
  return value.trim().replace(/^\.\//, "").replace(/^\/+/, "");
}

/** Literal part of a glob before its first wildcard. */
export function literalPrefix(glob: string): string {
  const g = normalize(glob);
  const idx = g.search(/[*?]/);
  return idx === -1 ? g : g.slice(0, idx);
}

/** Concrete example paths a glob matches, used to test overlap against another glob. */
export function samples(glob: string): string[] {
  const out = [
    glob
      .replace(/\*\*\//g, "")
      .replace(/\*\*/g, "x")
      .replace(/\*/g, "x")
      .replace(/\?/g, "x"),
  ];
  if (glob.endsWith("/**")) out.push(glob.slice(0, -3));
  return out;
}

function isDirPrefix(prefix: string, path: string): boolean {
  if (prefix === "") return false;
  const dir = prefix.endsWith("/") ? prefix : `${prefix}/`;
  return path === prefix || path.startsWith(dir) || dir.startsWith(path.endsWith("/") ? path : `${path}/`);
}

/**
 * Approximate overlap between two path globs: true when an example path of one
 * matches the other, or when their literal directory prefixes contain each other.
 * Errs toward reporting overlap for directory globs, never for unrelated trees.
 */
export function globsOverlap(a: string, b: string): boolean {
  const na = normalize(a);
  const nb = normalize(b);
  if (na === nb) return true;
  if (samples(na).some((s) => matchGlob(nb, s))) return true;
  if (samples(nb).some((s) => matchGlob(na, s))) return true;
  const pa = literalPrefix(na);
  const pb = literalPrefix(nb);
  const aWild = pa !== na;
  const bWild = pb !== nb;
  if (aWild && bWild) return isDirPrefix(trimToDir(pa), trimToDir(pb)) || isDirPrefix(trimToDir(pb), trimToDir(pa));
  if (aWild) return isDirPrefix(trimToDir(pa), nb);
  if (bWild) return isDirPrefix(trimToDir(pb), na);
  return false;
}

function trimToDir(prefix: string): string {
  const idx = prefix.lastIndexOf("/");
  return idx === -1 ? "" : prefix.slice(0, idx);
}

export function anyOverlap(as: readonly string[], bs: readonly string[]): boolean {
  return as.some((a) => bs.some((b) => globsOverlap(a, b)));
}
